import api from './api';
import { 
  type Promotion, 
  type PromotionCalculationRequest, 
  type PromotionCalculationResponse 
} from '@/types/promotion';

const promotionService = {
  /**
   * Calcular promociones aplicables para una orden
   */
  async calculatePromotions(request: PromotionCalculationRequest): Promise<PromotionCalculationResponse> {
    try {
      return await api.post('/promotions/calculate', request) as PromotionCalculationResponse;
    } catch (error: any) {
      console.error('Error calculating promotions:', error);
      // Return fallback response if API fails
      return {
        applied_promotions: [],
        total_discount: 0,
        final_total: request.order_total,
        eligible_promotions: [],
        ineligible_promotions: []
      };
    }
  },

  /**
   * Obtener todas las promociones activas
   */
  async getActivePromotions(): Promise<Promotion[]> {
    try {
      const response = await api.get('/promotions/active');
      return Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
    } catch (error: any) {
      console.error('Error fetching active promotions:', error);
      return [];
    }
  },

  /**
   * Obtener todas las promociones (para admin)
   */
  async getAllPromotions(): Promise<Promotion[]> {
    try {
      const response = await api.get('/promotions');
      // La API devuelve el array directamente, no en response.data
      return Array.isArray(response) ? response : Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      console.error('Error fetching promotions:', error);
      return [];
    }
  },

  /**
   * Obtener una promoción por ID (para admin)
   */
  async getPromotionById(promotionId: number): Promise<Promotion> {
    try {
      const response = await api.get(`/promotions/${promotionId}`);
      return response.data || response;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to get promotion');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Crear una nueva promoción (admin)
   */
  async createPromotion(promotion: Omit<Promotion, 'id' | 'created_at' | 'updated_at' | 'used_count'>): Promise<Promotion> {
    try {
      const response = await api.post('/promotions', promotion);
      return response.data || response;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to create promotion');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Actualizar una promoción existente (admin)
   */
  async updatePromotion(promotionId: number, promotion: Partial<Promotion>): Promise<Promotion> {
    try {
      const response = await api.put(`/promotions/${promotionId}`, promotion);
      return response.data || response;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to update promotion');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Eliminar una promoción (admin)
   */
  async deletePromotion(promotionId: number): Promise<void> {
    try {
      await api.delete(`/promotions/${promotionId}`);
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to delete promotion');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Activar/desactivar una promoción (admin)
   */
  async togglePromotion(promotionId: number, isActive: boolean): Promise<Promotion> {
    try {
      const response = await api.patch(`/promotions/${promotionId}/toggle`, { is_active: isActive });
      return response.data || response;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.detail || 'Failed to toggle promotion');
      } else if (error.request) {
        throw new Error('No response received from the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Obtener estadísticas de uso de promociones (admin)
   */
  async getPromotionStats(promotionId?: number): Promise<{
    promotion_id: number;
    name: string;
    used_count: number;
    total_discount_given: number;
    average_discount_per_use: number;
    usage_by_day: Array<{
      date: string;
      usage_count: number;
      total_discount: number;
    }>;
  }[]> {
    try {
      const url = promotionId ? `/promotions/${promotionId}/stats` : '/promotions/stats';
      const response = await api.get(url);
      return Array.isArray(response.data) ? response.data : Array.isArray(response) ? response : [];
    } catch (error: any) {
      console.error('Error fetching promotion stats:', error);
      return [];
    }
  }
};

export default promotionService;
